/**
 * ThemeProvider component for runtime theming
 * Manages theme (light/dark) and density (dense/normal/comfortable)
 * Persists preferences in localStorage
 */

import { createContext, useState, useEffect, ReactNode } from 'react';
import { lightTheme, darkTheme, applyTheme } from '@/constants/theme';

export type ThemeMode = 'light' | 'dark';
export type Density = 'dense' | 'normal' | 'comfortable';

interface ThemeContextValue {
  theme: ThemeMode;
  density: Density;
  toggleTheme: () => void;
  setTheme: (theme: ThemeMode) => void;
  setDensity: (density: Density) => void;
}

export const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: ThemeMode;
  defaultDensity?: Density;
}

const THEME_STORAGE_KEY = 'app-theme';
const DENSITY_STORAGE_KEY = 'app-density';

export function ThemeProvider({
  children,
  defaultTheme = 'light',
  defaultDensity = 'normal',
}: ThemeProviderProps) {
  // Initialize theme from localStorage, system preference, or default
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored && (stored === 'light' || stored === 'dark')) {
      return stored as ThemeMode;
    }

    // Check system preference
    if (typeof window !== 'undefined' && window.matchMedia) {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      return prefersDark ? 'dark' : 'light';
    }

    return defaultTheme;
  });

  // Initialize density from localStorage or default
  const [density, setDensityState] = useState<Density>(() => {
    const stored = localStorage.getItem(DENSITY_STORAGE_KEY);
    if (stored && (stored === 'dense' || stored === 'normal' || stored === 'comfortable')) {
      return stored as Density;
    }
    return defaultDensity;
  });

  // Apply theme CSS variables on mount and when theme/density changes
  useEffect(() => {
    const themeColors = theme === 'light' ? lightTheme : darkTheme;
    applyTheme(themeColors);

    // Add/remove dark class on html element
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    // Apply density as a data attribute for CSS selectors
    root.setAttribute('data-density', density);

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', theme === 'dark' ? '#18181b' : '#ffffff');
    }
  }, [theme, density]);

  // Persist theme to localStorage
  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  // Persist density to localStorage
  useEffect(() => {
    localStorage.setItem(DENSITY_STORAGE_KEY, density);
  }, [density]);

  // Listen for system theme preference changes (only if user hasn't manually set a preference)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      // Only auto-update if there's no stored preference (user hasn't manually chosen)
      const hasStoredPreference = localStorage.getItem(THEME_STORAGE_KEY);
      if (!hasStoredPreference) {
        setThemeState(e.matches ? 'dark' : 'light');
      }
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    // Older browsers
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  const toggleTheme = () => {
    setThemeState((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const setTheme = (newTheme: ThemeMode) => {
    setThemeState(newTheme);
  };

  const setDensity = (newDensity: Density) => {
    setDensityState(newDensity);
  };

  const value: ThemeContextValue = {
    theme,
    density,
    toggleTheme,
    setTheme,
    setDensity,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
